import React, { useCallback, useMemo, useState } from 'react'
import { Box, Menu, MenuItem } from '@mui/material'
import { getTwinSecurityKey } from '../../../util/security'
import { Exchange, exchangeToJSON } from '../../../proto/generated/common'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import useSetPrimarySecurity from '../../../hooks/useSetPrimarySecurity'
import { useSelector } from 'react-redux'
import { selectExchangesForSecurity } from '../../../selectors/security'
import { generatePath, useMatch, useNavigate } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import { Security } from '../../../proto/generated/security'

interface ExchangeMenuButtonProps {
    security: Security
    readOnly?: boolean
}

function exchangeButtonPropsAreEqual(prev: ExchangeMenuButtonProps, next: ExchangeMenuButtonProps) {
    return (
        prev.readOnly === next.readOnly &&
        prev.security?.exchange === next.security?.exchange &&
        prev.security?.code === next.security?.code &&
        prev.security?.isin === next.security?.isin &&
        prev.security?.companyCode === next.security?.companyCode
    )
}

const EXCHANGE_LABEL_SX = { padding: 0, margin: 0, maxHeight: '24px', fontSize: '14px' }

const ExchangeMenuButton: React.FC<ExchangeMenuButtonProps> = React.memo(({ security, readOnly }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const setPrimarySecurity = useSetPrimarySecurity()
    const exchanges = useSelector(selectExchangesForSecurity(security))
    const isSymbolPage = useMatch(ROUTES.symbol)
    const navigate = useNavigate()
    const securities = useMemo(() => [security], [security])

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (exchanges.length > 1) {
                setAnchorEl(event.currentTarget)
            }
        },
        [exchanges]
    )

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const handleExchangeSelect = useCallback(
        (exchange: Exchange) => {
            const key = getTwinSecurityKey(security, exchange)
            if (!key) {
                handleClose()
                return
            }
            setPrimarySecurity(key, securities, false)
            if (isSymbolPage) {
                navigate(generatePath(ROUTES.symbol, { symbol: key }), { replace: true })
            }
            handleClose()
        },
        [security, setPrimarySecurity, securities, isSymbolPage, navigate, handleClose]
    )

    const currentLabel = useMemo(() => exchangeToJSON(security.exchange).toUpperCase(), [security.exchange])

    if (readOnly) {
        return (
            <NonInteractiveTooltip title={'Exchange'} arrow placement="top">
                <Box component="span" sx={{ ...EXCHANGE_LABEL_SX, color: 'primary.main' }}>
                    {currentLabel}
                </Box>
            </NonInteractiveTooltip>
        )
    }

    if (exchanges.length <= 1) {
        return null
    }

    return (
        <>
            <NonInteractiveTooltip title={'Exchange'} arrow placement="top">
                <ExtraSmallButton
                    size="small"
                    variant="text"
                    color="primary"
                    sx={EXCHANGE_LABEL_SX}
                    onClick={handleClick}
                >
                    {currentLabel}
                </ExtraSmallButton>
            </NonInteractiveTooltip>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose} MenuListProps={{ dense: true }}>
                {exchanges.map((exchange, index) => (
                    <MenuItem key={index} onClick={() => handleExchangeSelect(exchange)} selected={exchange === security.exchange}>
                        {exchangeToJSON(exchange).toUpperCase()}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}, exchangeButtonPropsAreEqual)

export default ExchangeMenuButton
